// PhotoSwipe v5 ライトボックス連携。
// JS・CSS とも npm パッケージ（photoswipe）から読み込む。
import 'photoswipe/dist/photoswipe.css';      // v5 ベース CSS
import './photoswipe-skin.scss';               // プロジェクト独自スキン（ベースの後に読み込む）
// Webpack 4 は package.json の "exports" フィールド非対応のため、
// 'photoswipe/lightbox' ではなく dist の明示パスで import する。
import PhotoSwipeLightbox from 'photoswipe/dist/photoswipe-lightbox.esm.js';
import PhotoSwipe from 'photoswipe/dist/photoswipe.esm.js';

// 閉じる/矢印アイコン（--primarycolor のライン画。旧スキンの見た目を踏襲）。
// stroke は currentColor とし、色は photoswipe-skin.scss の .pswp__icn で付与する。
// belt.js など他モジュールからも再利用するため export する。
export const closeSVG =
  '<svg aria-hidden="true" class="pswp__icn" viewBox="0 0 24 24" width="24" height="24">' +
  '<path d="M5 5 L19 19 M19 5 L5 19" fill="none" stroke="currentColor" stroke-width="1" vector-effect="non-scaling-stroke"/></svg>';
export const arrowPrevSVG =
  '<svg aria-hidden="true" class="pswp__icn" viewBox="0 0 24 24" width="24" height="24">' +
  '<path d="M22 12 H3 M10 5 L3 12 L10 19" fill="none" stroke="currentColor" stroke-width="1" vector-effect="non-scaling-stroke"/></svg>';
export const arrowNextSVG =
  '<svg aria-hidden="true" class="pswp__icn" viewBox="0 0 24 24" width="24" height="24">' +
  '<path d="M2 12 H21 M14 5 L21 12 L14 19" fill="none" stroke="currentColor" stroke-width="1" vector-effect="non-scaling-stroke"/></svg>';

export function setCaptionContent(el, html) {
  const captionHTML = html || '';
  el.innerHTML = captionHTML;
  el.classList.toggle('is-empty', !captionHTML.trim());
}

// キャプションの有無に関係なく、画面の上下に共通の余白（4rem）を確保する。
export function captionPaddingFn() {
  const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
  return { top: rootFontSize * 4, bottom: rootFontSize * 4, left: 0, right: 0 };
}

// 画面いっぱいまで拡大してフィットさせるズームレベル。
// PhotoSwipe 標準の 'fit' は原寸（等倍）が上限のため、画面より小さい画像だと
// まわりに余白が残る。ここでは上限を設けず常にビューポートにフィットさせる。
// belt.js のライトボックスからも共用する。
export function fitToViewportZoomLevel(zoomLevels) {
  const { x: areaW, y: areaH } = zoomLevels.panAreaSize;
  const { x: imgW, y: imgH } = zoomLevels.elementSize;
  if (!imgW || !imgH) return 1;
  return Math.min(areaW / imgW, areaH / imgH);
}

// 非同期生成した big がS3へ未転送・転送失敗の場合だけ、同期転送済みの原本で再読み込みする。
export function registerPhotoSwipeImageFallback(lightbox) {
  lightbox.addFilter('domItemData', (itemData, element, linkEl) => {
    if (linkEl && linkEl.dataset.pswpFallbackSrc) {
      itemData.fallbackSrc = linkEl.dataset.pswpFallbackSrc;
    }
    return itemData;
  });

  lightbox.on('loadError', ({ content, slide }) => {
    const fallbackSrc = content.data && content.data.fallbackSrc;
    if (!fallbackSrc || content.data.fallbackAttempted || fallbackSrc === content.data.src) return;

    content.data.fallbackAttempted = true;
    content.data.src = fallbackSrc;
    // PhotoSwipeが先に出したエラー要素を除き、同じContentを原本URLで読み直す。
    if (slide && slide.container) slide.container.innerText = '';
    content.isAttached = false;
    content.load(false, true);
  });
}

export function initPhotoSwipeFromDOM(gallerySelector = '.js-pswp-container') {
  const lightbox = new PhotoSwipeLightbox({
    gallery: gallerySelector,
    children: '.js-pswp-open', // WYSIWYG 本文内の <a> を拾わないよう専用クラスで限定
    pswpModule: PhotoSwipe,    // 静的に渡し単一バンドルにまとめる（動的 import チャンクを避ける）

    // 見た目・挙動（旧 v4 設定の引き継ぎ）
    bgOpacity: 1,
    spacing: 0.12,
    paddingFn: captionPaddingFn,
    loop: true,
    pinchToClose: true,
    closeOnVerticalDrag: true,
    allowPanToNext: true,
    arrowKeys: true,
    escKey: true,
    trapFocus: true,
    showHideAnimationType: 'none', // 旧挙動と同じ即時表示。ズーム演出が欲しければ 'zoom'

    // ズーム機能を無効化（常に画面フィット表示・手動拡大なし）。
    // 原寸を上限にせず画面いっぱいまで拡大する（fitToViewportZoomLevel を参照）。
    // 3つのズームレベルを揃えると、ツールバーのズームボタンも自動で非表示になる。
    initialZoomLevel: fitToViewportZoomLevel,
    secondaryZoomLevel: fitToViewportZoomLevel,
    maxZoomLevel: fitToViewportZoomLevel,
    imageClickAction: 'close', // 画像クリックは閉じる（旧 'zoom-or-close' からズームを除去）
    doubleTapAction: false,    // ダブルタップ/ダブルクリックでのズームを無効化

    // カスタムアイコン
    closeSVG,
    arrowPrevSVG,
    arrowNextSVG,
  });

  registerPhotoSwipeImageFallback(lightbox);

  // キャプション：各 <figure class="js-pswp-element"> 内の
  // <figcaption class="js-pswp-caption"> を読み取って表示する。
  // キャプションがない画像では要素を非表示にする。
  lightbox.on('uiRegister', () => {
    lightbox.pswp.ui.registerElement({
      name: 'js-pswp-caption',
      order: 9,
      isButton: false,
      appendTo: 'root',
      onInit: (el, pswp) => {
        el.className = 'pswp__custom-caption';
        const updateCaption = () => {
          const src = pswp.currSlide && pswp.currSlide.data && pswp.currSlide.data.element;
          const fig = src && src.closest('.js-pswp-element');
          const cap = fig && fig.querySelector('.js-pswp-caption');
          setCaptionContent(el, cap ? cap.innerHTML : '');
        };
        pswp.on('change', updateCaption);
        updateCaption();
      },
    });
  });

  lightbox.init();

  // URL ハッシュ深リンク #&pid=<1始まり画像>&gid=<1始まりギャラリー> を維持。
  const hash = window.location.hash.substring(1);
  if (hash.length >= 5) {
    const params = {};
    hash.split('&').forEach((pair) => {
      const [key, value] = pair.split('=');
      if (key && value) params[key] = value;
    });
    const pid = parseInt(params.pid, 10);
    const gid = parseInt(params.gid, 10);
    if (pid && gid) {
      const galleryEl = document.querySelectorAll(gallerySelector)[gid - 1];
      if (galleryEl) lightbox.loadAndOpen(pid - 1, { gallery: galleryEl });
    }
  }

  // 旧コードと同じく後始末関数を返す（呼び出し側は任意で利用）。
  return () => lightbox.destroy();
}
